import { ReactNode } from 'react';
import styled from '@emotion/styled';

const PlanWrapper = styled.div<{ $best?: boolean; $selected?: boolean; $shadow?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: ${({ $shadow }) => ($shadow ? 'none' : '1px solid #d8d8d8')};
  border-radius: 12px;
  padding: ${({ $shadow }) => ($shadow ? '18px 16px' : '8px 16px')};
  position: relative;
  cursor: pointer;
  box-shadow: ${({ $shadow }) => ($shadow ? '0px 0px 10px 0px #0000001a' : 'none')};
  font-size: ${({ $shadow }) => ($shadow ? '15px' : 'inherit')};
  line-height: ${({ $shadow }) => ($shadow ? '100%' : 'inherit')};
  letter-spacing: ${({ $shadow }) => ($shadow ? '0.87px' : 'inherit')};
  color: ${({ $shadow }) => ($shadow ? '#7e7e7e' : 'inherit')};
  font-weight: ${({ $shadow }) => ($shadow ? '500' : 'inherit')};

  &:hover {
    border-color: ${({ $shadow }) => ($shadow ? 'transparent' : '#b8b8b8')};
  }
`;

const Badge = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.87px;
  color: white;
  background: #31bcf9;
  padding: 2px 10px;
  border-radius: 16px;
  position: absolute;
  top: -10px;
  right: 20px;
  font-weight: 600;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.div<{ $shadow?: boolean }>`
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.87px;
  color: ${({ $shadow }) => ($shadow ? '#4c4c4c' : '#6b6b6b')};
  text-transform: uppercase;
`;

const Price = styled.div<{ $shadow?: boolean }>`
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.87px;
  color: #6b6b6b;
  margin-top: ${({ $shadow }) => ($shadow ? '4px' : '0')};
`;

const Description = styled.div<{ $shadow?: boolean }>`
  font-size: 12px;
  color: #8a8a8a;
  line-height: 1.4;
  margin-top: ${({ $shadow }) => ($shadow ? '6px' : '4px')};
  max-width: 280px;
`;

const Radio = styled.div<{ $selected?: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid ${({ $selected }) => ($selected ? '#31bcf9' : '#d8d8d8')};
  position: relative;
  transition: border-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #31bcf9;
    transform: translate(-50%, -50%) scale(${({ $selected }) => ($selected ? 1 : 0.5)});
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
`;

interface PlanCardProps {
  name?: string | ReactNode;
  price?: string | ReactNode;
  description?: string;
  best?: boolean;
  selected?: boolean;
  noRadio?: boolean;
  shadow?: boolean;
  onSelect?: () => void;
  children?: ReactNode;
}

export function PlanCard({
  name,
  price,
  description,
  best = false,
  selected = false,
  noRadio = false,
  shadow = false,
  onSelect,
  children,
}: PlanCardProps) {
  return (
    <PlanWrapper $best={best} $selected={selected} $shadow={shadow} onClick={onSelect}>
      {best && <Badge>Best choice</Badge>}
      <Left>
        {children
          ? (
              children
            )
          : (
              <>
                <Name $shadow={shadow}>{name}</Name>
                {price && <Price $shadow={shadow}>{price}</Price>}
                {description && <Description $shadow={shadow}>{description}</Description>}
              </>
            )}
      </Left>
      {!noRadio && <Radio $selected={selected} />}
    </PlanWrapper>
  );
}
